import { useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import { usePageTitle } from '../../hooks/usePageTitle';
import { STRIPE_LINKS } from '../../lib/stripe';
import { CITIES, getCityBySlug, getNearbyCityData } from '../../data/broward-cities';
import { SERVICES, getServiceBySlug } from '../../data/services';
import {
  generateCityIntro,
  monthlySearchEstimate,
  scannedCount,
} from '../../data/city-intros';

const SITE_ORIGIN = 'https://floridaaimarketingexperts.com';

export default function ServiceCityPage() {
  const { service: serviceSlug, city: citySlug } = useParams<{
    service: string;
    city: string;
  }>();

  const service = serviceSlug ? getServiceBySlug(serviceSlug) : undefined;
  const city = citySlug ? getCityBySlug(citySlug) : undefined;

  if (!service || !city) {
    return <Navigate to="/service-areas" replace />;
  }

  const intro = generateCityIntro(city, service);
  const scanned = scannedCount(city, service);
  const monthlySearches = monthlySearchEstimate(city, service);
  const nearby = getNearbyCityData(city.nearby);
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);

  const title = `${service.name} Agency in ${city.name}, FL — Retainer-Led AI Marketing`;
  const description = `Retainer-led ${service.display_short} for ${city.name}, FL businesses. AI visibility, voice capture, attribution reporting, and integrated growth — not another vendor.`;
  const canonical = `${SITE_ORIGIN}/services/${service.slug}/${city.slug}`;

  const schema = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: `Florida AI Marketing Experts — ${service.name} in ${city.name}`,
        description,
        url: canonical,
        areaServed: [
          { '@type': 'City', name: city.name, containedInPlace: 'Broward County, FL' },
          ...nearby.map((n) => ({ '@type': 'City', name: n.name })),
        ],
        address: {
          '@type': 'PostalAddress',
          addressLocality: city.name,
          addressRegion: 'FL',
          addressCountry: 'US',
        },
        serviceType: `${service.name} Agency`,
        priceRange: '$$$-$$$$',
        telephone: '+1-954-296-4896',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `How fast can FAME start delivering ${service.display_short} wins in ${city.name}?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `Most ${city.name} retainer clients see first wins inside week two. We assess your ${service.display_short} stack on day one, ship the first installs by day ten, and have weekly reporting live by week three.`,
            },
          },
          {
            '@type': 'Question',
            name: `Is this a retainer or a one-time engagement?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `Our flagship is a monthly retainer — Starter ($2,500/mo), Growth ($5,000/mo), or Dominance ($10,000/mo). We also offer T8 FASS at $1,997/mo and a $1,497 voice-AI setup if you want to start narrower and scale in.`,
            },
          },
          {
            '@type': 'Question',
            name: `How is this different from a typical ${city.name} marketing agency?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `We\'re AI-first: generative search visibility, voice capture, and attribution are baked in from day one, not bolted on. One team owns the whole funnel — no handoffs between an SEO vendor, an ad vendor, and a "reputation" vendor.`,
            },
          },
        ],
      },
    ],
    [service, city, nearby, canonical, description]
  );

  usePageTitle({
    title: `${title} | FAME`,
    description,
    keywords: `${service.keyword_primary} ${city.name}, ${service.keyword_secondary} ${city.name}, AI marketing agency ${city.name}, generative engine optimization ${city.name}`,
    canonical,
    schema,
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white grid-bg pt-36 pb-20">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 30% 0%, rgba(13,148,136,0.14), transparent 58%)' }}
        ></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="eyebrow mb-4">
            {city.name}, FL · {city.county} County · Retainer-led
          </div>
          <h1 className="h-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.02em] mb-6">
            {service.name} Agency in {city.name}, FL
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mb-8 text-slate-600 font-body font-light">{intro.opening}</p>
          <p className="text-base md:text-lg max-w-3xl text-slate-600 font-body font-light mb-10">{intro.middle}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={STRIPE_LINKS.T10_STARTER}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary px-6 py-3"
            >
              Start Retainer — $2,500/mo →
            </a>
            <Link
              to="/audit"
              className="btn-secondary px-6 py-3"
            >
              Free AI Assessment
            </Link>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-navy-50 py-8 border-y border-navy-100">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-center text-sm text-navy-900 font-semibold mb-4">
            Serving {city.name} + {nearby[0].name} + {nearby[1].name}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { label: 'AI-first agency', icon: 'ri-cpu-line' },
              { label: 'One team, one funnel', icon: 'ri-team-line' },
              { label: 'Weekly attribution', icon: 'ri-line-chart-line' },
              { label: 'No 12-month lock-ins', icon: 'ri-shield-check-line' },
            ].map((p) => (
              <div key={p.label} className="flex items-center justify-center gap-2 text-navy-900">
                <i className={`${p.icon} text-teal-600 text-xl`}></i>
                <span className="text-sm font-medium">{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The problem */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-navy-900 mb-4">
            The 3 problems FAME sees most in {city.name}
          </h2>
          <p className="text-navy-600 text-lg mb-10 max-w-3xl">
            We&apos;ve run {service.display_short} assessments on {city.name} brands and their top three
            competitors. The pattern repeats every time.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {service.pain_points.map((pp, i) => (
              <div
                key={i}
                className="bg-navy-50 rounded-xl p-6 border-l-4 border-teal-500"
              >
                <div className="text-teal-600 font-bold text-sm mb-2">PROBLEM #{i + 1}</div>
                <p className="text-navy-700">{pp}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tier ladder */}
      <section className="py-16 bg-navy-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-navy-900 mb-4">
            Three ways to start — {city.name} brands pick the entry point
          </h2>
          <p className="text-navy-600 text-lg mb-10 max-w-3xl">
            Every engagement is month-to-month. Scale up when the math works, pause when it
            doesn&apos;t — no 12-month contracts.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <TierCard
              tag="T5 + T7 · Start narrow"
              price="$1,497 + $297/mo"
              name="Voice AI Setup"
              blurb={`7-day voice agent install for ${city.name} — plugs into your existing line, books jobs, writes to CRM.`}
              href={STRIPE_LINKS.T5_RECEPTIONIST_SETUP}
              accent="border-navy-100"
            />
            <TierCard
              tag="T10 Starter · Most popular"
              price="$2,500/mo"
              name="Agency Retainer"
              blurb={`Single pillar focus (your pick), AI visibility tracking, monthly reporting, quarterly strategy. Flagship offer.`}
              href={STRIPE_LINKS.T10_STARTER}
              accent="border-teal-500"
              highlight
            />
            <TierCard
              tag="T8 Standard · Full funnel"
              price="$1,997/mo"
              name="FASS Standard"
              blurb={`Florida AI Sales Team — receptionist + lead follow-up + local SEO + content, tuned to ${city.name}.`}
              href={STRIPE_LINKS.T8_FASS_STANDARD}
              accent="border-navy-100"
            />
          </div>
          <p className="text-center text-xs text-navy-600 mt-6">
            Scaling fast? See{' '}
            <a
              href={STRIPE_LINKS.T10_GROWTH}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-700 font-semibold underline"
            >
              Growth ($5,000/mo)
            </a>{' '}
            and{' '}
            <a
              href={STRIPE_LINKS.T10_DOMINANCE}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-700 font-semibold underline"
            >
              Dominance ($10,000/mo)
            </a>
            .
          </p>
        </div>
      </section>

      {/* Local proof */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block bg-teal-50 border border-teal-200 rounded-full px-5 py-2 text-sm text-teal-700 font-semibold mb-6">
            Local proof · {city.name}
          </div>
          <p className="text-3xl md:text-4xl font-display font-bold text-navy-900 mb-4">
            We&apos;ve run AI visibility scans on{' '}
            <span className="text-teal-600">{scanned}</span> {city.name} brands
          </p>
          <p className="text-navy-600 text-lg mb-2">{intro.proof}</p>
          <p className="text-navy-500 text-sm">
            Every scan surfaces the exact assistant-citation gaps, GBP issues, and tracking holes your
            competitors are already exploiting.
          </p>
        </div>
      </section>

      {/* ROI calculator */}
      <RoiCalculator
        service={service}
        city={city}
        monthlySearches={monthlySearches}
      />

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-navy-900 mb-8 text-center">
            {service.name} + {city.name}: common questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: `How fast can FAME start delivering ${service.display_short} wins in ${city.name}?`,
                a: `Most ${city.name} retainer clients see first wins inside week two. We assess your ${service.display_short} stack on day one, ship the first installs by day ten, and have weekly reporting live by week three.`,
              },
              {
                q: `Is this a retainer or a one-time engagement?`,
                a: `Both options exist. Flagship is monthly retainer: Starter ($2,500/mo), Growth ($5,000/mo), or Dominance ($10,000/mo). We also offer T8 FASS at $1,997/mo and a $1,497 voice-AI setup if you want to start narrower. Every neighborhood in ${city.name} — ${city.notable_neighborhoods.slice(0, 3).join(', ')} — is in scope.`,
              },
              {
                q: `How is this different from a typical ${city.name} marketing agency?`,
                a: `We\'re AI-first. Generative search visibility (ChatGPT, Perplexity, Gemini), voice capture, and revenue attribution are built in from day one, not bolted on. One team owns the whole funnel — no handoffs between three vendors.`,
              },
            ].map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-display font-bold mb-4">
            Free AI Assessment for {city.name} brands
          </h2>
          <p className="text-lg md:text-xl mb-8 text-navy-100">
            20-minute visibility scan + a written summary of what&apos;s working, what&apos;s
            broken, and what a 90-day roadmap looks like — across ChatGPT, Perplexity, Gemini, and
            Google.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/audit"
              className="btn-primary px-8 py-4"
            >
              Run My Assessment →
            </Link>
            <Link
              to={service.industry_route}
              className="btn-secondary px-8 py-4"
            >
              See the full {service.name} pillar
            </Link>
          </div>
        </div>
      </section>

      {/* Internal linking */}
      <section className="py-16 bg-navy-50">
        <div className="max-w-6xl mx-auto px-6">
          <h3 className="text-2xl font-display font-bold text-navy-900 mb-6">
            {service.name} in nearby {city.county} County cities
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {CITIES.filter((c) => c.slug !== city.slug)
              .slice(0, 4)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/services/${service.slug}/${c.slug}`}
                  className="bg-white rounded-lg p-4 border border-navy-100 hover:border-teal-300 hover:shadow transition"
                >
                  <div className="text-xs text-navy-500 uppercase">We also serve</div>
                  <div className="text-navy-900 font-semibold">
                    {service.name} in {c.name}
                  </div>
                </Link>
              ))}
          </div>

          <h3 className="text-2xl font-display font-bold text-navy-900 mb-6">
            Other pillars in {city.name}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {otherServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}/${city.slug}`}
                className="bg-white rounded-lg p-4 border border-navy-100 hover:border-teal-300 hover:shadow transition"
              >
                <div className="text-xs text-navy-500 uppercase">{city.name}</div>
                <div className="text-navy-900 font-semibold">{s.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-900 text-white/80 py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <div className="text-white font-bold mb-1">Florida AI Marketing Experts</div>
            <div className="text-sm">
              Serving {city.name}, {city.county} County, and all of Florida.
            </div>
          </div>
          <div className="text-sm">
            <a href="tel:+19542964896" className="mr-4 hover:text-white">
              (954) 296-4896
            </a>
            <Link to="/service-areas" className="hover:text-white">
              All service areas →
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TierCard({
  tag,
  price,
  name,
  blurb,
  href,
  accent,
  highlight,
}: {
  tag: string;
  price: string;
  name: string;
  blurb: string;
  href: string;
  accent: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`bg-white rounded-xl p-6 border-2 ${accent} ${
        highlight ? 'shadow-xl scale-[1.02]' : ''
      }`}
    >
      <div className="text-xs uppercase tracking-wide text-navy-500 mb-2">{tag}</div>
      <div className="text-3xl font-bold text-navy-900 mb-1">{price}</div>
      <div className="text-lg font-semibold text-teal-700 mb-3">{name}</div>
      <p className="text-sm text-navy-600 mb-5">{blurb}</p>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="block text-center btn-primary py-2"
      >
        Start here →
      </a>
    </div>
  );
}

function RoiCalculator({
  service,
  city,
  monthlySearches,
}: {
  service: {
    avg_deal_value: number;
    avg_deal_value_label: string;
    name: string;
    display_short: string;
  };
  city: { name: string };
  monthlySearches: number;
}) {
  const [newDealsPerQuarter, setNewDealsPerQuarter] = useState(3);
  const quarterlyRevenue = newDealsPerQuarter * service.avg_deal_value;
  const annualRevenue = quarterlyRevenue * 4;
  const retainerCost = 2500 * 12;
  const net = annualRevenue - retainerCost;

  return (
    <section className="py-16 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
          Retainer ROI calculator — {service.name} in {city.name}
        </h2>
        <p className="text-navy-100 mb-8">
          Drag the slider to the number of additional qualified deals you&apos;d expect the
          retainer to produce per quarter. Average client deal value on FAME engagements is around{' '}
          {service.avg_deal_value_label}. Local buyer demand estimate: ~{monthlySearches}{' '}
          high-intent searches a month.
        </p>
        <div className="bg-white/10 border border-white/20 rounded-xl p-6 backdrop-blur">
          <label className="block text-sm text-white/70 mb-2">
            Additional qualified deals per quarter
          </label>
          <input
            type="range"
            min={1}
            max={20}
            step={1}
            value={newDealsPerQuarter}
            onChange={(e) => setNewDealsPerQuarter(Number(e.target.value))}
            className="w-full mb-4"
          />
          <div className="text-2xl font-bold text-white mb-6">
            {newDealsPerQuarter} deals / quarter
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-white/70 text-xs uppercase">Avg deal value</div>
              <div className="text-3xl font-bold text-white">{service.avg_deal_value_label}</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-white/70 text-xs uppercase">Annual new revenue</div>
              <div className="text-3xl font-bold text-orange-400">
                ${annualRevenue.toLocaleString()}
              </div>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-white/70 text-xs uppercase">Net of $2,500/mo retainer</div>
              <div className="text-3xl font-bold text-orange-300">${net.toLocaleString()}</div>
            </div>
          </div>
          <p className="text-center text-white/70 text-sm mt-6">
            At {service.avg_deal_value_label} deals, the retainer breaks even at{' '}
            <span className="text-orange-400 font-bold">
              {Math.ceil(retainerCost / service.avg_deal_value)} new deals/year
            </span>
            . Everything above that is margin.
          </p>
        </div>
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-navy-100 rounded-lg">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex justify-between items-center p-4 text-left font-semibold text-navy-900"
      >
        <span>{q}</span>
        <span className="text-teal-600">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="p-4 pt-0 text-navy-600">{a}</div>}
    </div>
  );
}
